/**
 * The ink a reviewer can draw in (PLAT-11284).
 *
 * Structured after `src/utility/savedSegmentColors.ts` — a small fixed palette
 * plus a name function for accessible labels — but deliberately its own list.
 * Ink is not a saved-segment colour and not a `--content-*` token: those carry
 * meaning elsewhere in the product, and reusing them would say something about
 * the mark that is not true. Ink means nothing; it exists so overlapping notes
 * stay apart and each reviewer can pick a colour they can find again.
 *
 * Stored as hex rather than a CSS variable name because the marks are rendered
 * on the public share page too, outside this app's stylesheet.
 *
 * Chosen to stay legible over arbitrary video, which is a harder constraint
 * than sitting on our own surfaces: high chroma, and spread far enough apart in
 * hue to stay distinguishable against a busy frame.
 */
export interface AnnotationInk {
  hex: string;
  /** Accessible label — the swatch is a colour, so it needs words too. */
  name: string;
}

export const ANNOTATION_INK_PALETTE: readonly AnnotationInk[] = [
  { hex: '#e91ee9', name: 'Magenta' },
  { hex: '#e0a91a', name: 'Gold' },
  { hex: '#22c55e', name: 'Green' },
  { hex: '#f97316', name: 'Orange' },
];

/**
 * Fallback when a reviewer has no colour of their own yet. Orange rather than
 * the first swatch: it reads on the widest range of footage, and it is the
 * default in the tool this was modelled on.
 */
export const DEFAULT_INK = '#f97316';

/**
 * Stroke width as a fraction of frame height, so thickness tracks the player's
 * size instead of pinning to a pixel count. ~0.6% of frame height reads as a
 * confident marker line without burying what it points at.
 */
export const DEFAULT_INK_WIDTH = 0.006;

/** Human-readable name for a hex, for labels and analytics. */
export function inkName(hex: string): string {
  return (
    ANNOTATION_INK_PALETTE.find(
      ink => ink.hex.toLowerCase() === hex.toLowerCase(),
    )?.name ?? hex
  );
}
